import {
  ArrowLeftOutlined,
  CalendarOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  EditOutlined,
  EnvironmentOutlined,
  FileTextOutlined,
  GlobalOutlined,
  LinkedinOutlined,
  LinkOutlined,
  MailOutlined,
  QuestionCircleOutlined,
  TagsOutlined,
  TeamOutlined,
  TrophyOutlined,
  TwitterOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Alert,
  Avatar,
  Badge,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Empty,
  List,
  message,
  Progress,
  Row,
  Space,
  Spin,
  Statistic,
  Table,
  Tabs,
  Tag,
  Timeline,
  Typography,
} from 'antd';
import dayjs from 'dayjs';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import CheckinAccess from '../components/CheckinAccess';
import EventAnalytics from '../components/EventAnalytics';
import EventProgramEditor from '../components/EventProgramEditor';
import api from '../config/api';
import { fetchAllTickets } from '../config/fetchAllTickets';
import { formatPercent } from '../utils/formatPercent';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;

interface EventDetails {
  id: string;
  title: string;
  slug: string;
  description?: string;
  startDate: string;
  endDate: string;
  location?: string;
  venue?: string;
  coverImage?: string;
  logo?: string;
  isActive: boolean;
  isPublic: boolean;
  maxAttendees?: number;
  timezone?: string;
  /** `HU` / `EN` / `SR` / `NL` — a beléptető-link nyelvét ez adja (#86). */
  language?: string;
  organization?: {
    id: string;
    name: string;
    description?: string;
    aboutOrganizer?: string;
    logo?: string;
  };
  _count?: {
    tickets: number;
    sessions: number;
    speakers: number;
    sponsors: number;
  };
  sessions?: any[];
  speakers?: any[];
  sponsors?: any[];
  ticketTypes?: any[];
  faqs?: any[];
  documents?: any[];
}

const EventDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [event, setEvent] = useState<EventDetails | null>(null);
  const [sessions, setSessions] = useState<any[]>([]);
  const [speakers, setSpeakers] = useState<any[]>([]);
  const [sponsors, setSponsors] = useState<any[]>([]);
  const [ticketTypes, setTicketTypes] = useState<any[]>([]);
  const [attendees, setAttendees] = useState<any[]>([]);
  /** A szerver szerinti résztvevőszám (#164) — a fül felirata EBBŐL készül. */
  const [attendeesTotal, setAttendeesTotal] = useState(0);
  const [faqs, setFaqs] = useState<any[]>([]);
  const [documents, setDocuments] = useState<any[]>([]);
  const [attendeeStats, setAttendeeStats] = useState<any>(null);
  const { t, i18n } = useTranslation();

  useEffect(() => {
    if (id) {
      fetchEventDetails();
      fetchSessions();
      fetchSpeakers();
      fetchSponsors();
      fetchTicketTypes();
      fetchAttendees();
      fetchFAQs();
      fetchDocuments();
      fetchAttendeeStats();
    }
  }, [id]);

  const fetchEventDetails = async () => {
    try {
      const response = await api.get(`/events/${id}`);
      setEvent(response.data);
    } catch (error) {
      message.error(t('eventDetails.loadFailed'));
      navigate('/events');
    } finally {
      setLoading(false);
    }
  };

  const fetchSessions = async () => {
    try {
      const response = await api.get(`/sessions?eventId=${id}`);
      // API returns array directly, not wrapped in data property
      setSessions(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch sessions:', error);
      setSessions([]);
    }
  };

  const fetchSpeakers = async () => {
    try {
      const response = await api.get(`/speakers?eventId=${id}`);
      // API returns array directly, not wrapped in data property
      setSpeakers(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch speakers:', error);
      setSpeakers([]);
    }
  };

  const fetchSponsors = async () => {
    try {
      const response = await api.get(`/sponsors?eventId=${id}`);
      // API returns array directly, not wrapped in data property
      setSponsors(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch sponsors:', error);
      setSponsors([]);
    }
  };

  const fetchTicketTypes = async () => {
    try {
      const response = await api.get(`/tickets/types/event/${id}`);
      setTicketTypes(response.data || []);
    } catch (error) {
      console.error('Failed to fetch ticket types:', error);
    }
  };

  /**
   * A résztvevő-fül listája (#164).
   *
   * Korábban ez `GET /tickets?eventId=…` volt, oldalméret nélkül — a szerver
   * alapértelmezése `limit = 20`, tehát egy 176 jegyes eseményből 20 sor jött
   * vissza, és a fül felirata ezt a 20-at írta ki darabszámnak.
   *
   * A darabszám külön állapotban él, és a `meta.total`-ból jön: a lista
   * rövidülhet (felső korlát, megszakadt lapozás), a SZÁM nem hazudhat.
   */
  const fetchAttendees = async () => {
    try {
      const result = await fetchAllTickets({ eventId: id });
      setAttendees(result?.data ?? []);
      setAttendeesTotal(result?.total ?? 0);
    } catch (error) {
      console.error('Failed to fetch attendees:', error);
      setAttendees([]);
      setAttendeesTotal(0);
    }
  };

  const fetchFAQs = async () => {
    try {
      const response = await api.get(`/faqs?eventId=${id}`);
      // Handle both response formats
      setFaqs(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch FAQs:', error);
      setFaqs([]);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await api.get(`/documents?eventId=${id}`);
      // Handle both response formats
      setDocuments(Array.isArray(response.data) ? response.data : response.data.data || []);
    } catch (error) {
      console.error('Failed to fetch documents:', error);
      setDocuments([]);
    }
  };

  const fetchAttendeeStats = async () => {
    try {
      const response = await api.get(`/tickets/statistics/${id}`);
      setAttendeeStats(response.data);
    } catch (error) {
      console.error('Failed to fetch attendee statistics:', error);
    }
  };

  if (loading) {
    return (
      <div
        style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (!event) {
    return <Empty description={t('eventDetails.notFound')} />;
  }

  const sessionColumns = [
    {
      title: t('eventDetails.columnTitle'),
      dataIndex: 'title',
      key: 'title',
    },
    {
      title: t('eventDetails.columnType'),
      dataIndex: 'type',
      key: 'type',
      render: (type: string) => <Tag color="blue">{type}</Tag>,
    },
    {
      title: t('eventDetails.columnTime'),
      key: 'time',
      render: (_: any, record: any) => (
        <Space direction="vertical" size={0}>
          <Text>{dayjs(record.startTime).format('HH:mm')}</Text>
          <Text type="secondary">{dayjs(record.endTime).format('HH:mm')}</Text>
        </Space>
      ),
    },
    {
      title: t('eventDetails.columnLocation'),
      dataIndex: 'location',
      key: 'location',
    },
  ];

  const attendeeColumns = [
    {
      title: t('eventDetails.columnTicketNumber'),
      dataIndex: 'ticketNumber',
      key: 'ticketNumber',
      render: (ticketNumber: string) => (
        <Text copyable strong>
          {ticketNumber}
        </Text>
      ),
    },
    {
      title: t('eventDetails.columnName'),
      key: 'name',
      render: (_: any, record: any) => {
        const firstName = record.User?.firstName || record.firstName || '';
        const lastName = record.User?.lastName || record.lastName || '';
        const name = `${firstName} ${lastName}`.trim();
        return (
          <Space>
            <Avatar icon={<UserOutlined />} />
            <Text>{name || t('eventDetails.guest')}</Text>
          </Space>
        );
      },
    },
    {
      title: t('eventDetails.columnEmail'),
      key: 'email',
      render: (_: any, record: any) => record.User?.email || record.email || '-',
    },
    {
      title: t('eventDetails.columnTicketType'),
      key: 'ticketType',
      render: (_: any, record: any) => <Tag color="blue">{record.TicketType?.name || '-'}</Tag>,
    },
    {
      title: t('eventDetails.columnStatus'),
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const colors: any = {
          PENDING: 'default',
          PAID: 'green',
          USED: 'blue',
          CANCELLED: 'red',
        };
        return <Tag color={colors[status] || 'default'}>{status}</Tag>;
      },
    },
    {
      title: t('eventDetails.columnCheckedIn'),
      key: 'checkedIn',
      render: (_: any, record: any) => (
        <Tag color={record.checkedIn ? 'green' : 'default'}>
          {record.checkedIn ? t('eventDetails.yes') : t('eventDetails.no')}
        </Tag>
      ),
    },
    {
      title: t('eventDetails.columnCheckinTime'),
      dataIndex: 'checkedInAt',
      key: 'checkedInAt',
      render: (date: string) => (date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/events')}>
          {t('eventDetails.backToEvents')}
        </Button>
        <Button onClick={() => navigate(`/events/${event.id}/settings`)}>
          {t('eventDetails.settings')}
        </Button>
        <Button onClick={() => navigate(`/events/${event.id}/email-templates`)}>
          {t('eventDetails.emailTemplates')}
        </Button>
      </Space>

      {/* Event Header */}
      <Card style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={6}>
            {event.logo ? (
              <img
                src={event.logo}
                alt={event.title}
                style={{
                  width: '100%',
                  maxWidth: 200,
                  height: 'auto',
                  borderRadius: 8,
                }}
              />
            ) : (
              <div
                style={{
                  width: 200,
                  height: 200,
                  background: '#f0f0f0',
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <CalendarOutlined style={{ fontSize: 48, color: '#999' }} />
              </div>
            )}
          </Col>
          <Col span={18}>
            <Space direction="vertical" size="large" style={{ width: '100%' }}>
              <div>
                <Title level={2} style={{ marginBottom: 8 }}>
                  {event.title}
                </Title>
                <Space>
                  <Badge
                    status={event.isActive ? 'success' : 'default'}
                    text={event.isActive ? t('eventDetails.active') : t('eventDetails.inactive')}
                  />
                  <Badge
                    status={event.isPublic ? 'processing' : 'warning'}
                    text={event.isPublic ? t('eventDetails.public') : t('eventDetails.private')}
                  />
                </Space>
              </div>

              <Descriptions column={2}>
                <Descriptions.Item label={t('eventDetails.labelOrganization')}>
                  {event.organization?.name}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelSlug')}>
                  /{event.slug}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelStartDate')}>
                  <CalendarOutlined /> {dayjs(event.startDate).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelEndDate')}>
                  <CalendarOutlined /> {dayjs(event.endDate).format('YYYY-MM-DD HH:mm')}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelLocation')}>
                  <EnvironmentOutlined /> {event.location || t('eventDetails.online')}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelVenue')}>
                  {event.venue || '-'}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelTimezone')}>
                  <GlobalOutlined /> {event.timezone || 'UTC'}
                </Descriptions.Item>
                <Descriptions.Item label={t('eventDetails.labelMaxAttendees')}>
                  <TeamOutlined /> {event.maxAttendees || t('eventDetails.unlimited')}
                </Descriptions.Item>
              </Descriptions>

              {event.description && (
                <div>
                  <Text strong>{t('eventDetails.labelDescription')}</Text>
                  <Paragraph style={{ marginTop: 8 }}>{event.description}</Paragraph>
                </div>
              )}
            </Space>
          </Col>
        </Row>
      </Card>

      {/* Statistics */}
      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('eventDetails.statTotalAttendees')}
              value={attendeeStats?.total || 0}
              prefix={<TeamOutlined />}
              suffix={event.maxAttendees ? `/ ${event.maxAttendees}` : ''}
            />
            {event.maxAttendees && (
              <Progress
                percent={Math.round(((attendeeStats?.total || 0) / event.maxAttendees) * 100)}
                strokeColor="#52c41a"
                showInfo={false}
              />
            )}
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('eventDetails.statCheckedIn')}
              value={attendeeStats?.checkedIn || 0}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: '#52c41a' }}
            />
            <Text type="secondary">
              {t('eventDetails.checkInRate', {
                rate: formatPercent(attendeeStats?.checkInRate, i18n.language),
              })}
            </Text>
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('eventDetails.statSessions')}
              value={sessions.length}
              prefix={<ClockCircleOutlined />}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title={t('eventDetails.statSpeakers')}
              value={speakers.length}
              prefix={<UserOutlined />}
            />
          </Card>
        </Col>
      </Row>

      {/* Cover Image */}
      {event.coverImage && (
        <Card style={{ marginBottom: 24 }}>
          <img
            src={event.coverImage}
            alt={t('eventDetails.coverAlt')}
            style={{
              width: '100%',
              maxHeight: 400,
              objectFit: 'cover',
              borderRadius: 8,
            }}
          />
        </Card>
      )}

      {/* Tabs with Event Details */}
      <Card>
        <Tabs defaultActiveKey="sessions">
          <TabPane tab={t('eventDetails.tabProgram', { count: sessions.length })} key="sessions">
            {event ? (
              <EventProgramEditor
                eventId={event.id}
                eventStartDate={event.startDate}
                eventEndDate={event.endDate}
                sessions={sessions}
                onChange={fetchSessions}
              />
            ) : (
              <Empty description={t('eventDetails.loadingEvent')} />
            )}
          </TabPane>

          <TabPane tab={t('eventDetails.tabSpeakers', { count: speakers.length })} key="speakers">
            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 3, lg: 4 }}
              dataSource={speakers}
              renderItem={(speaker: any) => (
                <List.Item>
                  <Card>
                    <Space direction="vertical" align="center" style={{ width: '100%' }}>
                      <Avatar
                        size={64}
                        src={speaker.photo}
                        icon={!speaker.photo && <UserOutlined />}
                      />
                      <Title level={5}>{speaker.name}</Title>
                      {speaker.title && <Text type="secondary">{speaker.title}</Text>}
                      {speaker.company && <Text>{speaker.company}</Text>}
                      <Space>
                        {speaker.email && (
                          <Button
                            type="link"
                            icon={<MailOutlined />}
                            href={`mailto:${speaker.email}`}
                          />
                        )}
                        {speaker.linkedin && (
                          <Button
                            type="link"
                            icon={<LinkedinOutlined />}
                            href={speaker.linkedin}
                            target="_blank"
                          />
                        )}
                        {speaker.twitter && (
                          <Button
                            type="link"
                            icon={<TwitterOutlined />}
                            href={speaker.twitter}
                            target="_blank"
                          />
                        )}
                      </Space>
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          </TabPane>

          <TabPane tab={t('eventDetails.tabSponsors', { count: sponsors.length })} key="sponsors">
            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
              dataSource={sponsors}
              renderItem={(sponsor: any) => (
                <List.Item>
                  <Card>
                    <Space direction="vertical" align="center" style={{ width: '100%' }}>
                      {sponsor.logo ? (
                        <img
                          src={sponsor.logo}
                          alt={sponsor.name}
                          style={{ maxWidth: 150, maxHeight: 80 }}
                        />
                      ) : (
                        <TrophyOutlined style={{ fontSize: 48 }} />
                      )}
                      <Title level={5}>{sponsor.name}</Title>
                      <Tag color="gold">{sponsor.sponsorLevel?.name || sponsor.level}</Tag>
                      {sponsor.website && (
                        <Button
                          type="link"
                          icon={<LinkOutlined />}
                          href={sponsor.website}
                          target="_blank"
                        >
                          {t('eventDetails.website')}
                        </Button>
                      )}
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          </TabPane>

          <TabPane tab={t('eventDetails.tabTickets', { count: ticketTypes.length })} key="tickets">
            <List
              grid={{ gutter: 16, xs: 1, sm: 2, md: 3 }}
              dataSource={ticketTypes}
              renderItem={(ticket: any) => (
                <List.Item>
                  <Card>
                    <Space direction="vertical" style={{ width: '100%' }}>
                      <Title level={4}>{ticket.name}</Title>
                      {ticket.description && <Text>{ticket.description}</Text>}
                      <Statistic
                        value={ticket.price}
                        prefix="€"
                        precision={2}
                        valueStyle={{ color: '#1890ff' }}
                      />
                      <Progress
                        percent={Math.round(
                          ((ticket.quantity - ticket.available) / ticket.quantity) * 100
                        )}
                        format={() => `${ticket.available} / ${ticket.quantity}`}
                        status="active"
                      />
                      {ticket.startSale && (
                        <Text type="secondary">
                          {t('eventDetails.saleStarts', {
                            date: dayjs(ticket.startSale).format('YYYY-MM-DD'),
                          })}
                        </Text>
                      )}
                      {ticket.endSale && (
                        <Text type="secondary">
                          {t('eventDetails.saleEnds', {
                            date: dayjs(ticket.endSale).format('YYYY-MM-DD'),
                          })}
                        </Text>
                      )}
                    </Space>
                  </Card>
                </List.Item>
              )}
            />
          </TabPane>

          <TabPane
            // #164 — a szerver szerinti darabszám, nem a betöltött tömb hossza.
            tab={t('eventDetails.tabAttendees', { count: attendeesTotal })}
            key="attendees"
          >
            {attendees.length > 0 ? (
              <Table
                dataSource={attendees}
                columns={attendeeColumns}
                rowKey="id"
                pagination={{
                  defaultPageSize: 10,
                  // A `total` a BETÖLTÖTT sorok száma, mert ez a tábla
                  // kliensoldalon lapoz: az antd `ceil(total / pageSize)`
                  // oldalt hirdet, és a betöltött soroknál több oldal üresen
                  // nyílna meg. Ugyanaz a hibakép, amit a #164 a Jegyek
                  // lapozójáról levesz — ott a szűretlen `stats.total`
                  // csinálta, itt a `meta.total` csinálná.
                  total: attendees.length,
                  // A FELIRAT viszont továbbra is a szerver számát mondja, és
                  // ezért nem az argumentumából dolgozik: a lista rövidülhet,
                  // a szám nem hazudhat. A kettő két külön igény, és a
                  // `pagination.total` csak az egyiket szolgálja ki.
                  showTotal: () => t('eventDetails.attendeesTotal', { count: attendeesTotal }),
                }}
              />
            ) : (
              <Empty description={t('eventDetails.noAttendees')} />
            )}
          </TabPane>

          <TabPane tab={t('eventDetails.tabFaqs', { count: faqs.length })} key="faqs">
            {faqs.length > 0 ? (
              <List
                dataSource={faqs}
                renderItem={(faq: any) => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<QuestionCircleOutlined style={{ fontSize: 24, color: '#1890ff' }} />}
                      title={<Text strong>{faq.question}</Text>}
                      description={faq.answer}
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={t('eventDetails.noFaqs')} />
            )}
          </TabPane>

          <TabPane
            tab={t('eventDetails.tabDocuments', { count: documents.length })}
            key="documents"
          >
            {documents.length > 0 ? (
              <List
                dataSource={documents}
                renderItem={(doc: any) => (
                  <List.Item
                    actions={[
                      <Button
                        type="link"
                        icon={<FileTextOutlined />}
                        href={doc.url}
                        target="_blank"
                      >
                        {t('eventDetails.view')}
                      </Button>,
                    ]}
                  >
                    <List.Item.Meta
                      avatar={<FileTextOutlined style={{ fontSize: 24 }} />}
                      title={doc.title}
                      description={
                        <Space direction="vertical">
                          {doc.description && <Text>{doc.description}</Text>}
                          <Space>
                            <Tag>{doc.type}</Tag>
                            <Text type="secondary">
                              {dayjs(doc.uploadedAt).format('YYYY-MM-DD')}
                            </Text>
                          </Space>
                        </Space>
                      }
                    />
                  </List.Item>
                )}
              />
            ) : (
              <Empty description={t('eventDetails.noDocuments')} />
            )}
          </TabPane>

          <TabPane tab={t('eventDetails.tabCheckinAccess')} key="checkin">
            <CheckinAccess
              eventId={event.id}
              eventTitle={event.title}
              eventLanguage={event.language}
            />
          </TabPane>

          <TabPane tab={t('eventDetails.tabAnalytics')} key="analytics">
            <EventAnalytics eventId={event.id} />
          </TabPane>

          {event.organization?.aboutOrganizer && (
            <TabPane tab={t('eventDetails.tabAboutOrganizer')} key="organizer">
              <Card>
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                  {event.organization.logo && (
                    <img
                      src={event.organization.logo}
                      alt={event.organization.name}
                      style={{ maxWidth: 200 }}
                    />
                  )}
                  <Title level={3}>{event.organization.name}</Title>
                  {event.organization.description && (
                    <Paragraph>{event.organization.description}</Paragraph>
                  )}
                  <Divider />
                  <div
                    dangerouslySetInnerHTML={{ __html: event.organization.aboutOrganizer }}
                    style={{ lineHeight: 1.8 }}
                  />
                </Space>
              </Card>
            </TabPane>
          )}
        </Tabs>
      </Card>
    </div>
  );
};

export default EventDetails;
